/** Ключ admin-сессии cloud-admin на кассе. */
const KEY = 'pos_cloud_admin_token'
const REFRESH_KEY = 'pos_cloud_admin_refresh'

import { secureGet, secureRemove, secureSet } from '@core/native/secureStorage'

export const CLOUD_ADMIN_SESSION_EVENT = 'pos-cloud-admin-session'

function notify(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CLOUD_ADMIN_SESSION_EVENT))
  }
}

const SECURE_REFRESH_KEY = 'cloud_refresh_token'

/** Admin-токен только в памяти вкладки; не хранится в Dexie. */
export function getCloudAdminToken(): string {
  try {
    return sessionStorage.getItem(KEY) ?? ''
  } catch {
    return ''
  }
}

export function setCloudAdminToken(token: string): void {
  try {
    if (token) sessionStorage.setItem(KEY, token)
    else sessionStorage.removeItem(KEY)
  } catch {
    /* sessionStorage недоступен — админ-сессия будет только в памяти */
  }
  notify()
}

export function clearCloudAdminToken(): void {
  setCloudAdminToken('')
  setCloudRefreshToken('')
}

export function getCloudRefreshToken(): string {
  try {
    return sessionStorage.getItem(REFRESH_KEY) ?? ''
  } catch {
    return ''
  }
}

export function setCloudRefreshToken(token: string): void {
  try {
    if (token) sessionStorage.setItem(REFRESH_KEY, token)
    else sessionStorage.removeItem(REFRESH_KEY)
  } catch {
    /* ignore */
  }
  void (token ? secureSet(SECURE_REFRESH_KEY, token) : secureRemove(SECURE_REFRESH_KEY)).catch(() => undefined)
}

let refreshInFlight: Promise<boolean> | null = null

/**
 * Обновить admin access-token по refresh-token (с ротацией).
 * Возвращает false, если сессия истекла/отозвана — тогда нужен повторный вход.
 */
export async function refreshCloudAdminSession(): Promise<boolean> {
  const refreshToken = getCloudRefreshToken()
  if (!refreshToken) return false
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const api = import.meta.env.VITE_API_URL ?? 'https://api.pospoint.ru/api/v1'
      const response = await fetch(`${api}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      })
      // 5xx/network — временная ошибка: сессию не сжигаем.
      if (response.status === 401 || response.status === 403) {
        clearCloudAdminToken()
        return false
      }
      if (!response.ok) return false
      const data = (await response.json()) as {
        token: string
        refresh_token: string
      }
      setCloudAdminToken(data.token)
      setCloudRefreshToken(data.refresh_token)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/**
 * Восстановить admin-сессию после перезапуска/обновления.
 * Access-token не хранится: берём refresh-token из Android Keystore.
 */
export async function restoreCloudAdminSession(): Promise<boolean> {
  if (getCloudAdminToken()) return true
  let token = getCloudRefreshToken()
  if (!token) {
    try {
      token = await secureGet(SECURE_REFRESH_KEY)
    } catch {
      token = ""
    }
  }
  if (!token) return false
  setCloudRefreshToken(token)
  return refreshCloudAdminSession()
}
